import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  ScrollArea,
  rem,
  Text,
  UnstyledButton,
  Group,
  Center,
  Select,
  Button,
  Checkbox,
  Pagination,
} from "@mantine/core";
import {
  IconSelector,
  IconChevronDown,
  IconChevronUp,
  IconTrash,
  IconEdit,
  IconBrandTabler,
  IconEye,
} from "@tabler/icons-react";
import { showErrorAlert, showSuccessAlert } from "../../helper/alerts";
import DeleteModal from "./DeleteModal";
import ViewMessageModal from "./ViewMessageModal";
import classes from "../BannerPage/Banner.module.css";
import { RowDataContactUs } from "./types";
import { useFetchRowData } from "../hooks/useFetchRowData";
import { contactUs } from "../types/RowDataTypes";
import { CustomTable } from "../Table/CustomTable";
import { SearchBar } from "../SearchBar/SearchBar";
import useAxiosPrivate from "../../auth/useAxiosPrivate";
import Header from "../static/Header";
// import getBannerTable from "../../DataDisplay/DataDisplayTable/BannerTable";
import getContactUsTable from "../../DataDisplay/DataDisplayTable/ContactUsTable";

import { formatYYYY_MM_DD_HH_mm } from "../../helper/DateTime";

// Searching and Sorting
enum ColumnHeaderToFilterPair {
    FULLNAME = "Name",
    PHONE = "Phone Number",
    EMAIL = "Email",
    MESSAGE = "Message",
    DATE = "Submitted On"
}

const colFilter = ColumnHeaderToFilterPair;
const colFilters: { [key in ColumnHeaderToFilterPair]: string } = {
    [colFilter.FULLNAME]: "fullname",
    [colFilter.PHONE]: "phone_number",
    [colFilter.EMAIL]: "email",
    [colFilter.MESSAGE]: "message",
    [colFilter.DATE]: "createdAt",
};

interface ThProps {
  children: React.ReactNode;
  sortable: boolean;
  reversed: boolean;
  sorted: boolean;
  onSort(): void;
}

function Th({ children, sortable, reversed, sorted, onSort }: ThProps) {
  const Icon = sorted
    ? reversed
      ? IconChevronUp
      : IconChevronDown
    : IconSelector;

  const allowedSorts = [
    colFilter.FULLNAME.toLowerCase(),
    colFilter.PHONE.toLowerCase(),
    colFilter.EMAIL.toLowerCase(),
  ];

  return (
    <Table.Th className={`${classes.th} ${sortable ? classes.sortable : ""}`}>
      {allowedSorts.includes(children!!.toString().toLowerCase()) ? (
        <UnstyledButton onClick={onSort} className={classes.control}>
          <Group justify="space-between">
            <Text className={classes.Text}>{children}</Text>
            <Center className={classes.icon}>
              <Icon style={{ width: rem(16), height: rem(16) }} stroke={1.5} />
            </Center>
          </Group>
        </UnstyledButton>
      ) : (
        <Text fw={500} fz="sm">
          {children}
        </Text>
      )}
    </Table.Th>
  );
}

const initialData: RowDataContactUs[] = [
  {
    id: "",
    fullname: "",
    email: "",
    phone_number: "",
    message: "",

    createdAt: "",
    updatedAt: "",
    action: "",
  },
];

function truncateText(text: string, wordLimit: number) {
    // Split the text into an array of words
    const words = text.split(' ');

    // Check if the number of words exceeds the limit
    if (words.length > wordLimit) {
        // Join the words back into a string with the limit and add ellipsis
        return words.slice(0, wordLimit).join(' ') + '...';
    }

    // If the text is within the limit, return it as is
    return text;
}

export function ContactUs() {
  const axiosPrivate = useAxiosPrivate();

  const [search, setSearch] = useState("");
  const [tableData, setTableData] = useState(initialData);
  const [sortBy, setSortBy] = useState<keyof RowDataContactUs | null>(null);
  const [reverseSortDirection, setReverseSortDirection] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  //Used in searching and when deleting to cause rerender to fetch data
  const [tableStateChanged, setTableStateChanged] = useState(0);

  const changeTableState = () => {
    setTableStateChanged(Math.random() * Math.random());
  };

  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  function resetPage() {
    if (currentPage !== 1) {
      setCurrentPage(1);
      return true;
    }
    return false;
  }

  const checkboxesFilter: JSX.Element[] = [];

  //Set allowed search filter checkbox to be shown
  const allowedFilters = [
    colFilters[colFilter.FULLNAME],
    colFilters[colFilter.EMAIL],
    colFilters[colFilter.PHONE],
  ];
  for (const [key, value] of Object.entries(colFilters)) {
    if (allowedFilters.includes(value)) {
      const checkboxFilter: JSX.Element = (
        <Checkbox value={value} label={key} />
      );
      checkboxesFilter.push(checkboxFilter);
    }
  }

  const [showFilterOptions, setShowFilterOptions] = useState(false);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;
    setSearch(input);
  };


  const fetchedData = useFetchRowData(
    itemsPerPage,
    currentPage,
    search,
    selectedFilters,
    tableStateChanged,
    sortBy ?? "createdAt",
    reverseSortDirection ? "DESC" : "ASC",
    contactUs,
    getContactUsTable().create(),
    "",
  );
  const itemsOnPage = useRef(0);
  const [totalRows, setTotalRows] = useState<number>(0);
  // const totalRows = fetchedData.totalRows;
  let rowData: RowDataContactUs[] | null = fetchedData.data as RowDataContactUs[];
  const isLoading: boolean = fetchedData.isLoading;
  const [rowDataBanner, setRowDataBanner] = useState<RowDataContactUs[] | null>(
    null
  );

  const [isViewBannerModalOpen, setViewBannerModalOpen] = useState(false);
  const [selectedRowForView, setSelectedRowForView] =
    useState<RowDataContactUs | null>(null);

  const [isDeleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [selectedRowToDelete, setSelectedRowToDelete] =
    useState<RowDataContactUs | null>(null);

  const handleDelete = (row: RowDataContactUs) => {
    setSelectedRowToDelete(row);
    setDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirmed = () => {
    axiosPrivate
      .post("/api/admin/contact-us/delete", {
        id: selectedRowToDelete!!.id,
      })
      .then((response) => {
        setDeleteConfirmationOpen(false);
        if (
          itemsOnPage.current == 1 &&
          currentPage !== 1 &&
          currentPage == Math.ceil(totalRows / itemsPerPage)
        )
          setCurrentPage((prev) => prev - 1);
        else changeTableState();
      })
      .catch((err) => {
        showErrorAlert("An error has occured");
      });

    setDeleteConfirmationOpen(false);
    setSelectedRowToDelete(null);
  };

  const handleDeleteCancelled = () => {
    setDeleteConfirmationOpen(false);
    setSelectedRowToDelete(null);
  };

  const handleView = (row: RowDataContactUs) => {
    setSelectedRowForView(row);
    setViewBannerModalOpen(true);
  };

  const setSorting = (field: keyof RowDataContactUs) => {
    const reversed = field === sortBy ? !reverseSortDirection : false;
    setReverseSortDirection(reversed);
    setSortBy(field);
    changeTableState();
  };

  const startIndex = (currentPage - 1) * itemsPerPage;

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleRowsPerPageChange = (value: number) => {
    setCurrentPage(1);
    setItemsPerPage(value);
  };

  useEffect(() => {
    setRowDataBanner(rowData);
    itemsOnPage.current = fetchedData.itemsOnPage;
    setTotalRows(fetchedData.totalRows);
  }, [rowData]);

  const rowsToMap = rowDataBanner ?? [];
  const rows = rowsToMap.map((row, index) => (
    <Table.Tr key={index}>
      <Table.Td>{startIndex + index + 1}</Table.Td>
      <Table.Td className={classes.titleColumn}>{row.fullname}</Table.Td>
      <Table.Td className={classes.imageLink}>{row.email}</Table.Td>
      {/* <Table.Td className={classes.colorAndButtonColumn}>{row.color}</Table.Td> */}
      <Table.Td className={classes.colorAndButtonColumn}>{row.phone_number}</Table.Td>
      <Table.Td className={classes.colorAndButtonColumn}>{truncateText(row.message, 8)}</Table.Td>
      <Table.Td className={classes.colorAndButtonColumn}>{formatYYYY_MM_DD_HH_mm(row.createdAt)}</Table.Td>

      <Table.Td className={classes.actionButtons}>
        <button onClick={() => handleView(row)} className={classes.viewButton}>
          <IconEye /> View
        </button>

        <button
          onClick={() => handleDelete(row)}
          className={classes.trashbutton}
        >
          <IconTrash /> Delete
        </button>
      </Table.Td>
    </Table.Tr>
  ));

  const handleSelectPage = (selectedPage: number) => {
    if (selectedPage != currentPage) setCurrentPage(selectedPage);
  };

  return (
    <div className={classes.bannerPageContainer}>
      <ScrollArea className={classes.parentContainer}>
        <Header title="Contact Us Messages" />
        <div className={classes.searchAndAddContainer}>
          <SearchBar
            isLoading={isLoading}
            resetPage={resetPage}
            changeTableState={changeTableState}
            search={search}
            handleSearch={handleSearch}
            setShowFilterOptions={setShowFilterOptions}
            showFilterOptions={showFilterOptions}
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
            checkboxesFilter={checkboxesFilter}
          />
          <div className={classes.AddButtonContainer}>
            {/* <Button className={classes.addButton} onClick={handleAddBanner}>
              <IconBrandTabler className={classes.iconBrandTabler} />
              Create Banner
            </Button> */}
          </div>
        </div>

        <ViewMessageModal
          isOpen={isViewBannerModalOpen}
          onClose={() => setViewBannerModalOpen(false)}
          bannerData={selectedRowForView || initialData[0]}
        />

        <DeleteModal
          isOpen={isDeleteConfirmationOpen}
          onClose={handleDeleteCancelled}
          onConfirm={handleDeleteConfirmed}
        />

        <CustomTable
          colHeaders={Object.values(ColumnHeaderToFilterPair)}
          filters={Object.values(colFilters)}
          reverseSortDirection={reverseSortDirection}
          sortBy={sortBy}
          setSorting={setSorting}
          customclasses={classes}
          Th={Th}
          rowData={rowDataBanner}
          rows={rows}
        />

        <div className={classes.paginationContainer}>
          <div className={classes.resultCount}>
            <Text>
              {`Showing `}
              <span style={{ fontWeight: "bold" }}>{startIndex + 1}</span>
              {`-`}
              <span style={{ fontWeight: "bold" }}>
                {Math.min(currentPage * itemsPerPage, totalRows)}
              </span>
              {` of `}
              <span style={{ fontWeight: "bold" }}>{totalRows}</span>
              {` results`}
            </Text>
          </div>

          <div className={classes.rightContainer}>
            <div className={classes.rowsPerPageSelector}>
              <Select
                placeholder="Rows per page"
                data={["10", "20", "50"]}
                value={itemsPerPage.toString()}
                onChange={(value) =>
                  handleRowsPerPageChange(parseInt(value as string, 10))
                }
              />
            </div>

            <div className={classes.paginationSelector}>
              <Pagination
                color="blueviolet"
                value={currentPage}
                total={Math.ceil(totalRows / itemsPerPage)}
                onNextPage={() => handlePageChange(currentPage + 1)}
                onPreviousPage={() => handlePageChange(currentPage - 1)}
                onChange={(value: number) => handleSelectPage(value)}
              />
            </div>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}

export default ContactUs;
